import React from 'react';
import {
 Database,
 Users,
 Activity,
 Calendar,
 Eye as ViewIcon,
 Loader2,
 ListChecks,
 XCircle,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminAuditTrail } from '../../hooks/useAdminAuditTrail';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminAuditTrail = () => {
 const {
 auditTrail,
 isLoading,
 error,
 filters,
 logTypeOptions,
 handleFilterChange,
 handleSearch,
 statistics: stats,
 getLogTypeColor
 } = useAdminAuditTrail();

 const columns = [
 {
 key: 'id',
 label: 'Log ID',
 sortable: true,
 width: 'w-32',
 render: (log) => (
 <span className="font-mono text-sm font-medium text-ink">
 #{log.id.substring(0, 8)}
 </span>
 )
 },
 {
 key: 'log_type',
 label: 'Type',
 sortable: true,
 width: 'w-32',
 render: (log) => {
 const color = getLogTypeColor(log.log_type);
 return (
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
 {log.log_type === 'admin_action' ? 'Admin Action' : 'User Activity'}
 </span>
 );
 }
 },
 {
 key: 'user',
 label: 'User/Admin',
 sortable: true,
 width: 'w-48',
 render: (log) => {
 const user = log.user || log.admin;
 return (
 <div className="min-w-0">
 <p className="text-sm font-medium text-ink truncate">
 {user?.name || 'System'}
 </p>
 <p className="text-xs text-ink-tertiary truncate">
 {user?.email || ''}
 </p>
 </div>
 );
 }
 },
 {
 key: 'action_type',
 label: 'Action Type',
 sortable: true,
 width: 'w-40',
 render: (log) => (
 <span className="text-sm font-medium text-ink capitalize">
 {log.action_type?.replace('_', ' ') || 'N/A'}
 </span>
 )
 },
 {
 key: 'action_description',
 label: 'Description',
 sortable: false,
 width: 'w-96',
 render: (log) => (
 <p className="text-sm text-ink truncate">
 {log.action_description || 'N/A'}
 </p>
 )
 },
 {
 key: 'target_type',
 label: 'Target',
 sortable: true,
 width: 'w-32',
 render: (log) => (
 log.target_type ? (
 <span className="text-sm text-ink capitalize">
 {log.target_type}
 </span>
 ) : (
 <span className="text-sm text-gray-400">N/A</span>
 )
 )
 },
 {
 key: 'ip_address',
 label: 'IP Address',
 sortable: true,
 width: 'w-32',
 render: (log) => (
 <span className="font-mono text-sm text-ink">
 {log.ip_address || 'N/A'}
 </span>
 )
 },
 {
 key: 'created_at',
 label: 'Date',
 sortable: true,
 width: 'w-32',
 render: (log) => (
 <div className="flex items-center space-x-2">
 <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium text-ink">
 {formatDate(log.created_at, 'medium')}
 </div>
 <div className="text-xs text-ink-tertiary">
 {formatTime(log.created_at)}
 </div>
 </div>
 </div>
 )
 }
 ];

 const actions = [
 {
 label: 'View Details',
 icon: ViewIcon,
 variant: 'default',
 onClick: (log) => {
 console.log('View audit trail entry:', log);
 }
 }
 ];

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Audit Trail"
 subtitle="View complete audit trail of all system activities and admin actions"
 />
 <div className="rounded-lg border border-red-200 bg-surface p-6 text-center shadow-sm">
 <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
 <h3 className="text-xl font-bold text-ink mb-2">Error Loading Audit Trail</h3>
 <p className="text-ink-secondary">
 {error?.response?.data?.message || 'Failed to load audit trail. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Audit Trail"
 subtitle="View complete audit trail of all system activities and admin actions"
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.auditTrail.title}
 steps={ADMIN_PAGE_GUIDES.auditTrail.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Actions</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{stats.total_actions || 0}</p>
 </div>
 <Database className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Admin Actions (24h)</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{stats.admin_actions_24h || 0}</p>
 </div>
 <Users className="w-8 h-8 text-ink-secondary" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">User Activities (24h)</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{stats.user_activities_24h || 0}</p>
 </div>
 <Activity className="w-8 h-8 text-ink" />
 </div>
 </div>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <SearchSelect
 label="Log Type"
 value={filters.log_type}
 onChange={(value) => handleFilterChange('log_type', value)}
 options={logTypeOptions}
 />
 <DatePicker
 label="From Date"
 value={filters.date_from}
 onChange={(value) => handleFilterChange('date_from', value)}
 />
 <DatePicker
 label="To Date"
 value={filters.date_to}
 onChange={(value) => handleFilterChange('date_to', value)}
 />
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading audit trail…</p>
 </div>
 ) : (
 <DataTable
 data={auditTrail}
 columns={columns}
 actions={actions}
 isLoading={false}
 searchPlaceholder="Search by user, action type, description, or IP address..."
 onSearch={handleSearch}
 emptyMessage="No audit trail entries found"
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>
 </div>
 );
};

export default AdminAuditTrail;
